import { apiFetch } from './api';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  role?: string;
  isEmailVerified: boolean;
  onboardingComplete: boolean;
  authProvider?: string;
  avatarUrl?: string | null;
  createdAt?: string;
}

interface AuthTokenResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface OTPResponse {
  success: boolean;
  message: string;
  email?: string;
  requiresEmailVerification?: boolean;
}

interface ResendOTPResponse {
  success: boolean;
  message: string;
  waitSeconds?: number;
}

interface GoogleAuthResponse extends AuthTokenResponse {
  isNewUser: boolean;
}

// Backend wraps all responses in { success, data }
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

export const authApi = {
  register: async (data: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  }) => {
    const body: Record<string, string> = {
      email: data.email,
      name: `${data.firstName} ${data.lastName}`.trim(),
    };
    if (data.phone) body.phone = data.phone;

    const res = await apiFetch<ApiResponse<{ user: AuthUser }>>('/v1/auth/register', {
      method: 'POST',
      body,
    });

    return {
      success: res.success,
      message: res.message || "Registration successful! We've sent a verification code to your email.",
      email: data.email,
    } satisfies OTPResponse;
  },

  login: async (data: { email?: string; phone?: string }) => {
    const body: Record<string, string> = {};
    if (data.email) body.email = data.email;
    if (data.phone) body.phone = data.phone;

    const res = await apiFetch<ApiResponse<{ requiresEmailVerification?: boolean }>>('/v1/auth/send-login-otp', {
      method: 'POST',
      body,
    });

    return {
      success: res.success,
      message: res.message || 'A sign-in code has been sent.',
      email: data.email,
      requiresEmailVerification: res.data?.requiresEmailVerification ?? false,
    } satisfies OTPResponse;
  },

  verifyOTP: async (data: { email?: string; phone?: string; otp: string; type: 'EMAIL_VERIFY' | 'LOGIN_OTP' }) => {
    const endpoint =
      data.type === 'EMAIL_VERIFY' ? '/v1/auth/verify-email' : '/v1/auth/verify-login-otp';
    const body: Record<string, string> = { otp: data.otp };
    if (data.email) body.email = data.email;
    if (data.phone) body.phone = data.phone;
    
    const res = await apiFetch<ApiResponse<AuthTokenResponse>>(endpoint, {
      method: 'POST',
      body,
    });

    if (!res.data) {
      throw new Error('Authentication response did not include user tokens.');
    }

    return res.data;
  },

  resendOTP: async (data: { email: string; type: 'EMAIL_VERIFY' | 'LOGIN_OTP' }) => {
    const res = await apiFetch<ApiResponse<null>>('/v1/auth/resend-otp', {
      method: 'POST',
      body: data,
    });

    return {
      success: res.success,
      message: res.message || 'A new verification code has been sent to your email.',
    } satisfies ResendOTPResponse;
  },

  googleAuth: async (accessToken: string) => {
    const res = await apiFetch<ApiResponse<GoogleAuthResponse>>('/v1/auth/google', { method: 'POST', body: { accessToken } });
    return res.data;
  },

  /**
   * Invite flow step 1 — validate token, send OTP to the invited email.
   * Returns email, isNewUser, inviterName, homeName.
   */
  inviteAuth: async (token: string) => {
    const res = await apiFetch<ApiResponse<{
      email: string;
      isNewUser: boolean;
      inviterName: string;
      homeName: string;
    }>>('/v1/auth/invite-auth', { method: 'POST', body: { token } });
    if (!res.data) throw new Error('Invalid invite response.');
    return res.data;
  },

  /**
   * Invite flow step 2 — verify OTP, auto-accept invite, return tokens.
   * name is only required for brand-new users.
   */
  verifyInviteOtp: async (data: { token: string; otp: string; name?: string }) => {
    const res = await apiFetch<ApiResponse<AuthTokenResponse>>('/v1/auth/verify-invite-otp', {
      method: 'POST',
      body: data,
    });
    if (!res.data) throw new Error('Authentication response missing.');
    return res.data;
  },

  /** Invite flow — resend OTP */
  resendInviteOtp: async (token: string) => {
    await apiFetch<ApiResponse<null>>('/v1/auth/resend-invite-otp', { method: 'POST', body: { token } });
  },

  getMe: async () => {
    const res = await apiFetch<ApiResponse<AuthUser>>('/v1/users/me');
    if (!res.data) {
      throw new Error('Authenticated user profile is missing from response.');
    }
    return res.data;
  },

  logout: async (refreshToken: string) => {
    const res = await apiFetch<ApiResponse<{ message: string }>>('/v1/auth/logout', { method: 'POST', body: { refreshToken } });
    return res.data;
  },
};
